import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useUser } from '@clerk/clerk-react';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { validateInviteCode } from '../api/friends';
import { toast } from 'react-hot-toast';
import { SignInButton, SignUpButton } from '@clerk/clerk-react';

export default function InviteHandler() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { user, isSignedIn, isLoaded } = useUser();
  const { openAuthModal } = useAuth();
  const [isValidating, setIsValidating] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthPrompt, setIsAuthPrompt] = useState(false);

  // Add diagnostics
  console.log('[InviteHandler] Component state:', {
    code,
    isLoaded,
    isSignedIn,
    isValidating,
    isAuthPrompt,
    error,
    user: user ? 'User object exists' : 'No user',
    timestamp: new Date().toISOString()
  });

  useEffect(() => {
    console.log('[InviteHandler] useEffect triggered:', {
      code,
      isLoaded,
      isSignedIn
    });

    if (!code) {
      console.log('[InviteHandler] No code provided, setting error');
      setError('Invalid invite link');
      setIsValidating(false);
      return;
    }

    // Wait for auth to load
    if (!isLoaded) {
      console.log('[InviteHandler] Auth not loaded yet, waiting...');
      return;
    }

    console.log('[InviteHandler] Auth loaded, calling handleInvite');
    // Handle the invite based on auth state
    handleInvite();
  }, [code, isLoaded, isSignedIn]);

  const handleInvite = async () => {
    console.log('[InviteHandler] handleInvite called:', {
      code,
      isSignedIn,
      isLoaded
    });
    
    if (!code) return;

    try {
      if (isSignedIn) {
        console.log('[InviteHandler] User is signed in, validating invite code');
        // User is logged in - validate the invite code and auto-send friend request
        setIsValidating(true);
        
        const result = await validateInviteCode(code);
        console.log('[InviteHandler] Validation result:', result);
        
        if (result.valid) {
          // Check if this is a new user welcome
          const showWelcome = localStorage.getItem('showWelcomeMessage');
          if (showWelcome === 'true') {
            localStorage.removeItem('showWelcomeMessage');
            toast.success(`Welcome to IS Game Hub! Friend request sent to ${result.inviterName}!`, {
              duration: 5000
            });
          } else {
            // Success - show message and redirect to friends page
            if (result.alreadyFriends) {
              toast.success(`You're already friends with ${result.inviterName}`);
            } else if (result.requestSent) {
              toast.success(`Friend request sent to ${result.inviterName}!`);
            } else if (result.requestPending) {
              toast.info(`You already have a pending friend request with ${result.inviterName}`);
            }
          }
          
          navigate('/friends');
        } else {
          // Invalid code
          setError(result.error || 'Invalid or expired invite link');
        }
      } else {
        console.log('[InviteHandler] User NOT signed in, storing code and showing auth prompt');
        // Store code in localStorage for after auth
        localStorage.setItem('pendingInviteCode', code);
        console.log('[InviteHandler] Stored pendingInviteCode:', code);
        
        // Show auth prompt for new users
        setIsAuthPrompt(true);
        setIsValidating(false);
        console.log('[InviteHandler] Set isAuthPrompt=true, isValidating=false');
        return;
      }
    } catch (err) {
      console.error('Error handling invite:', err);
      setError('Failed to process invite link');
    } finally {
      setIsValidating(false);
    }
  };

  if (isValidating || !isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner className="mb-4" />
          <p className="text-gray-600">Validating invite link...</p>
        </div>
      </div>
    );
  }

  if (isAuthPrompt) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="mb-4 text-4xl">🎉</div>
          <h2 className="text-2xl font-bold mb-2">You've Been Invited!</h2>
          <p className="text-gray-600 mb-6">Sign in or create an account to accept this friend invitation</p>
          
          <div className="space-y-3">
            <SignUpButton mode="modal">
              <button 
                onClick={() => localStorage.setItem('isNewRegistration', 'true')}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
              >
                Create New Account
              </button>
            </SignUpButton>
            
            <SignInButton mode="modal">
              <button className="w-full px-6 py-3 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 font-medium">
                Sign In to Existing Account
              </button>
            </SignInButton>
          </div>
          
          <p className="mt-4 text-sm text-gray-500">
            Your friend invitation will be processed automatically after you sign in.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="mb-4 text-4xl">❌</div>
          <h2 className="text-xl font-semibold mb-2">Invalid Invite Link</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return null;
}